import {
  CodeBlockTab,
  CodeBlockTabs,
  CodeBlockTabsList,
  CodeBlockTabsTrigger,
} from "fumadocs-ui/components/codeblock";
import { Children, isValidElement } from "react";
import type { ReactElement, ReactNode } from "react";

type CodeBlockProps = {
  title?: ReactNode;
  children?: ReactNode;
};

type CodeGroupProps = {
  children: ReactNode;
};

function findCodeTitle(child: ReactNode): string | undefined {
  if (!isValidElement<CodeBlockProps>(child)) return undefined;
  if (typeof child.props.title === "string") return child.props.title;

  for (const nestedChild of Children.toArray(child.props.children)) {
    const title = findCodeTitle(nestedChild);
    if (title) return title;
  }

  return undefined;
}

function getTabLabel(child: ReactNode, index: number) {
  const title = findCodeTitle(child);
  if (title) return title;

  return `Code ${index + 1}`;
}

export function CodeGroup({ children }: CodeGroupProps) {
  // fumadocs-mdx interleaves a text-only <Metadata> node before each fenced
  // block. Keep only the real code blocks (they carry `children`); otherwise
  // the Metadata nodes render as empty "Code N" tabs.
  const blocks = Children.toArray(children).filter(
    (child): child is ReactElement<CodeBlockProps> =>
      isValidElement(child) && (child.props as CodeBlockProps).children != null,
  );

  if (blocks.length <= 1) {
    return blocks[0] ?? null;
  }

  const tabs = blocks.map((block, index) => ({
    block,
    label: getTabLabel(block, index),
    value: `code-${index}`,
  }));
  const defaultTab = tabs[0];

  if (!defaultTab) return null;

  return (
    <CodeBlockTabs defaultValue={defaultTab.value}>
      <CodeBlockTabsList>
        {tabs.map(({ label, value }) => (
          <CodeBlockTabsTrigger key={value} value={value}>
            {label}
          </CodeBlockTabsTrigger>
        ))}
      </CodeBlockTabsList>
      {tabs.map(({ block, value }) => (
        <CodeBlockTab key={value} value={value}>
          {block}
        </CodeBlockTab>
      ))}
    </CodeBlockTabs>
  );
}
